/**
 * Atlas API Client
 *
 * Minimal HTTP client for communicating with the Atlas AI platform backend.
 * Keeps configuration centralized while remaining lightweight since Atlas
 * context sync operations are fire-and-forget (silent failure on errors).
 */

import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { getIdentityToken } from '@/services/http';

const baseURL = process.env.VUE_APP_ATLAS_API_URL || 'http://localhost:8000';

const atlasAxios: AxiosInstance = axios.create({
  baseURL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Get authorization headers for Atlas API requests.
 */
function getAuthHeaders(): Record<string, string> {
  const token = getIdentityToken() || '';
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

/**
 * GET request to Atlas API.
 */
export function get<T>(url: string, config: AxiosRequestConfig = {}): Promise<T> {
  return atlasAxios
    .get<T>(url, {
      ...config,
      headers: { ...getAuthHeaders(), ...config.headers },
    })
    .then((res) => res.data);
}

/**
 * POST request to Atlas API.
 */
export function post<T>(url: string, data?: any, config: AxiosRequestConfig = {}): Promise<T> {
  return atlasAxios
    .post<T>(url, data, {
      ...config,
      headers: { ...getAuthHeaders(), ...config.headers },
    })
    .then((res) => res.data);
}

/**
 * DELETE request to Atlas API.
 */
export function del<T>(url: string, config: AxiosRequestConfig = {}): Promise<T> {
  return atlasAxios
    .delete<T>(url, {
      ...config,
      headers: { ...getAuthHeaders(), ...config.headers },
    })
    .then((res) => res.data);
}

export default {
  get,
  post,
  del,
};
